import { Capacitor } from "@capacitor/core";
import { Layers } from "lucide-react";

import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";
import { openAndroidOverlaySettings } from "@/lib/native/rules";
import { useOptionalOnboardingAnalytics } from "@/lib/onboarding/OnboardingAnalyticsContext";

import { OnboardingIllustration } from "../OnboardingIllustration";
import { OnboardingVibesOverlay } from "../OnboardingVibesOverlay";
import { getOnboardingIllustrationDimensions } from "../onboardingImagePreload";
import { onboardingImageCandidates } from "../onboardingImageUrl";

/** Android-only: "Display over other apps" so the block shield can appear. */
export function OverlaySetupSlide() {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const dimensions = getOnboardingIllustrationDimensions(10);

  return (
    <div className="flex flex-1 flex-col items-center gap-6 pb-4">
      <div className="relative mx-auto w-fit max-w-[280px]">
        <OnboardingVibesOverlay variant="setup" />
        <OnboardingIllustration
          candidates={onboardingImageCandidates(10)}
          alt={t.onboarding.overlayImageAlt}
          intrinsicWidth={dimensions?.width}
          intrinsicHeight={dimensions?.height}
        />
      </div>

      <div className="flex w-full max-w-md flex-col gap-4 text-center">
        <h2 className="text-xl font-semibold leading-snug text-[oklch(92%_0.02_150)]">
          {t.onboarding.overlayTitle}
        </h2>
        <p className="text-base leading-7 text-[oklch(82%_0.025_155)]">
          {t.onboarding.overlayBody}
        </p>
      </div>

      <section className="liquid-glass-suggestion flex w-full max-w-md gap-3 p-4 text-left">
        <Layers size={20} className="mt-0.5 shrink-0 text-[oklch(78%_0.06_155)]" aria-hidden />
        <p className="text-sm leading-6 text-[oklch(80%_0.025_155)]">
          {t.onboarding.overlayNativeHint}
        </p>
      </section>
    </div>
  );
}

export function useOverlaySetupSlideAction(onAdvance: () => void, options?: { step?: number }) {
  const analyticsStep = options?.step ?? 7;
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const analytics = useOptionalOnboardingAnalytics();
  const isAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";

  async function handleOverlayCta() {
    analytics?.trackCtaClick(analyticsStep, "primary");
    if (!isAndroid) {
      analytics?.trackPermissionOutcome(analyticsStep, "overlay", "skipped_non_android");
      onAdvance();
      return;
    }
    try {
      await openAndroidOverlaySettings();
      analytics?.trackPermissionOutcome(analyticsStep, "overlay", "settings_opened");
    } catch {
      analytics?.trackPermissionOutcome(analyticsStep, "overlay", "error");
    }
    onAdvance();
  }

  function handleSkip() {
    analytics?.trackCtaClick(analyticsStep, "secondary");
    analytics?.trackPermissionOutcome(analyticsStep, "overlay", "skipped");
    onAdvance();
  }

  return {
    handleOverlayCta,
    handleSkip,
    ctaLabel: t.onboarding.overlayCta,
    secondaryCtaLabel: t.onboarding.overlayNotNow,
    overlayBusy: false,
  };
}
